import Box, { BoxProps } from '@material-ui/core/Box';
import Checkbox, { CheckboxProps } from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useContextCheckboxWithOptionalContent } from 'components/CheckboxWithOptionalContent';
import Hints, { HintsProps } from 'components/Hints';
import { useContextRadioButtonWithOptionalContent } from 'components/RadioButtonWithOptionalContent';
import { FieldInputProps } from 'formik';
import { isArray } from 'lodash';
import React, { ReactElement, ReactNode, useEffect } from 'react';
import styled from 'styled-components';
import { ReactComponent as CheckedIcon } from './CheckedIcon.svg';

type Props = {
  form: any;
  editable?: boolean;
  disabled?: boolean;
  label?: string | ReactNode;
  inline?: boolean;
  field: FieldInputProps<any>;
  labelBold?: boolean;
  suffix?: ReactElement;
  checkboxProps?: CheckboxProps;
  wrapperProps?: BoxProps;
  isPrint?: boolean;
} & HintsProps;

export const useStyles = makeStyles({
  wrapper: {
    padding: 0,
    margin: '0!important',
  },
  root: {
    padding: '0 8px 0 0!important',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  icon: {
    background: '#fff',
    border: '1px solid #B5B5C3',
    boxSizing: 'border-box',
    borderRadius: 6,
    width: 25,
    height: 25,
    '$root.Mui-focusVisible &': {
      outline: '2px auto rgba(19,124,189,.6)',
      outlineOffset: 2,
    },
    'input:hover ~ &': {
      backgroundColor: '#fff',
      border: '1px solid rgba(92, 171, 221, 0.8)',
    },
    'input:disabled ~ &': {
      boxShadow: 'none',
      background: 'rgba(243, 246, 249, 0.6)',
      border: ' 1px solid rgba(181, 181, 195, 0.6)',
    },
  },
  checkedIcon: {
    background: '#E1F0FF',
    border: '1px solid rgba(92, 171, 221, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    'input:hover ~ &': {
      backgroundColor: '#E1F0FF',
    },
    'input:disabled ~ &': {
      boxShadow: 'none',
      background: 'rgba(129, 136, 150, 0.1)',
      border: ' 1px solid #B5B5C3',
      '&>svg>path': {
        fill: 'rgba(129, 136, 150, 0.6)',
      },
      cursor: 'not-allowed',
    },
  },
});

export const FormControlLabelStyle = styled(FormControlLabel)<{
  $isPrint?: boolean;
  $isDatagrid?: boolean;
}>`
  .MuiFormControlLabel-label {
    font-weight: 500;
    font-size: ${({ $isPrint, $isDatagrid }) =>
      $isPrint ? '10pt' : $isDatagrid ? '12px' : '14px'} !important;
    line-height: 21px;
    cursor: pointer;
    color: ${({ theme, $isDatagrid }) => ($isDatagrid ? 'black' : theme.colors.dolphin)};
    &.Mui-disabled {
      color: ${({ $isPrint, $isDatagrid, theme }) =>
        $isPrint || $isDatagrid ? 'black' : theme.colors.dolphin} !important;
      cursor: not-allowed;
    }
    .MuiButtonBase-root {
      &.Mui-disabled {
        cursor: not-allowed;
      }
    }
  }
`;

const CheckboxWithLabel: React.VFC<Props> = ({
  form,
  editable,
  disabled,
  label,
  inline,
  field,
  labelBold,
  suffix,
  checkboxProps,
  wrapperProps,
  ...props
}) => {
  const { checked } = useContextCheckboxWithOptionalContent();
  const { valueChecked } = useContextRadioButtonWithOptionalContent();

  useEffect(() => {
    const data = form.getFieldMeta(field.name);

    if (
      (checked !== undefined && !checked && isArray(data.value) && data.value?.length !== 0) ||
      (valueChecked !== undefined &&
        !valueChecked &&
        isArray(data.value) &&
        data.value?.length !== 0)
    ) {
      form.setFieldValue(field.name, undefined);
    }
  }, [checked, valueChecked, field, form]);

  const classes = useStyles();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        ...wrapperProps,
      }}
    >
      <FormControlLabelStyle
        className={classes.wrapper}
        control={
          <Checkbox
            className={classes.root}
            sx={{
              '&:hover': { bgcolor: 'transparent' },
            }}
            disableRipple
            color="default"
            disabled={!editable || disabled}
            checkedIcon={
              <span className={clsx(classes.icon, classes.checkedIcon)}>
                <CheckedIcon />
              </span>
            }
            icon={<span className={classes.icon} />}
            inputProps={{ 'aria-label': `${field.name} checkbox` }}
            {...checkboxProps}
            {...field}
          />
        }
        label={label}
      />
      <span
        style={{
          position: 'relative',
          display: 'inline-flex',
        }}
      >
        {suffix}
      </span>
      <Hints {...props} />
    </Box>
  );
};

const AdaptMgCheckboxWithLabel: React.VFC<Props> = (props) => {
  return (
    <Box
      sx={{
        py: 1.25,
      }}
    >
      <CheckboxWithLabel {...props} />
    </Box>
  );
};

export default CheckboxWithLabel;
export { AdaptMgCheckboxWithLabel };
